import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText, Heart, Loader, MessageCircle, ThumbsDown, ThumbsUp } from "lucide-react";
import FooterNav from "../components/FooterNav";
import PostItem from "../components/PostItem";
import { fetchOwnPostActivity, type PostActivity, type PostActivityKind } from "../api/posts";
import { formatBlackspaceDateTime } from "../utils/time";
import "./MyActivity.css";

export default function MyActivity({ kind }: { kind: PostActivityKind }) {
  const [items, setItems] = useState<PostActivity[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestRef = useRef<AbortController | null>(null);
  const busyRef = useRef(false);
  const title = kind === "comments" ? "Comments" : "Emotions";
  const Icon = kind === "comments" ? MessageCircle : Heart;

  const load = useCallback(async (cursor: string | null = null) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const result = await fetchOwnPostActivity(kind, cursor, controller.signal);
      if (controller.signal.aborted) return;
      setItems((current) => {
        if (!cursor) return result.items;
        const existing = new Set(current.map((item) => item.id));
        return [...current, ...result.items.filter((item) => !existing.has(item.id))];
      });
      setNextCursor(result.nextCursor);
      setUsername(result.username.replace(/^@+/, ""));
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "Could not load your activity.");
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        busyRef.current = false;
        if (!controller.signal.aborted) setLoading(false);
      }
    }
  }, [kind]);

  useEffect(() => {
    void load();
    return () => {
      requestRef.current?.abort();
      requestRef.current = null;
      busyRef.current = false;
    };
  }, [load]);

  const profilePath = username ? "/user/" + encodeURIComponent(username) + "?tab=posts" : "";

  return (
    <div className="bs-activity-page bs-page">
      <main className="bs-activity-main">
        <Link to="/home" state={{ restoreFeed: true }} className="bs-activity-back"><ArrowLeft size={16} aria-hidden="true" />Feed</Link>
        <header className="bs-activity-heading">
          <span className="bs-activity-emblem"><Icon size={21} aria-hidden="true" /></span>
          <div><h1>{title}</h1><p>{kind === "comments" ? "Your part in the conversation." : "The posts and comments you reacted to."}</p></div>
        </header>
        <nav className="bs-activity-tabs" aria-label="Your activity">
          {profilePath ? <Link to={profilePath}><FileText size={16} aria-hidden="true" />Posts</Link> : <span aria-disabled="true"><FileText size={16} aria-hidden="true" />Posts</span>}
          <Link to="/activity/comments" aria-current={kind === "comments" ? "page" : undefined}><MessageCircle size={16} aria-hidden="true" />Comments</Link>
          <Link to="/activity/emotions" aria-current={kind === "emotions" ? "page" : undefined}><Heart size={16} aria-hidden="true" />Emotions</Link>
        </nav>

        <section className="bs-activity-list" aria-label={"Your " + kind} aria-busy={loading}>
          {items.map((item) => {
            const ReactionIcon = item.emotion === "love" ? Heart : item.emotion === "dislike" ? ThumbsDown : ThumbsUp;
            const ItemIcon = kind === "comments" ? MessageCircle : ReactionIcon;
            const action = kind === "comments" ? "You commented" : "You " + (item.emotion === "love" ? "loved" : item.emotion === "dislike" ? "disliked" : "liked") + (item.commentId ? " a comment" : " a post");
            return (
              <article key={item.id} className="bs-activity-card">
                <header className="bs-activity-card-heading">
                  <ItemIcon size={15} className={"bs-activity-tone-" + (item.emotion || "comment")} aria-hidden="true" />
                  <span>{action}</span>
                  <time dateTime={item.createdAt}>{formatBlackspaceDateTime(item.createdAt)}</time>
                </header>
                {item.content && <blockquote className="bs-activity-comment">{item.content}</blockquote>}
                <div className="bs-activity-context"><PostItem post={item.post} currentUsername={username} /></div>
                <Link className="bs-activity-open" to={"/post/" + encodeURIComponent(item.post.id)} state={{ from: "/activity/" + kind, label: title }}>
                  View conversation<ArrowUpRight size={14} aria-hidden="true" />
                </Link>
              </article>
            );
          })}
          {!loading && !error && items.length === 0 && (
            <div className="bs-activity-empty"><Icon size={25} aria-hidden="true" /><h2>No {kind} yet</h2><p>{kind === "comments" ? "Your comments will appear here." : "Your reactions will appear here."}</p></div>
          )}
          {error && <div className="bs-activity-error" role="alert"><p>{error}</p><button type="button" onClick={() => void load(items.length ? nextCursor : null)}>Try again</button></div>}
          {loading && <p className="bs-activity-loading" role="status"><Loader size={17} className="bs-activity-spinner" aria-hidden="true" />Loading your {kind}…</p>}
          {!loading && !error && nextCursor && <button type="button" className="bs-activity-more" onClick={() => void load(nextCursor)}>Show more</button>}
        </section>
      </main>
      <footer className="bs-activity-footer"><FooterNav /></footer>
    </div>
  );
}
